// App.js
import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import RepoAnalyzer from "./RepoAnalyzer";
import { UserDashboard } from "./UserDashboard";
import AdminDashboard from "./AdminDashboard";
import About from "./About";
import { useAuth, AuthProvider } from "./AuthContext";
import Auth from "./Auth";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import InviteAccept from "./InviteAccept";
import Landing from "./Landing";
import DemoTaskImportPrompt from "./DemoTaskImportPrompt";
import ErrorBoundary from "./ErrorBoundary";
import { canManageIntegrations, canMutateRepos } from "./orgPermissions";
import { parseTaskLocation, taskPath } from "./taskRoutes";
import { parseProductLocation, reviewsPath, tasksPath } from "./app/workspaceRoutes";
import { repositoryIdsFor, uniqueRepositories } from "./app/repositories";
import api, { getRepo } from "./api";
import AppShell from "./app/AppShell";
import GuidesPage from "./guides/GuidesPage";
import { NewTaskPage, TaskDetailPage, TasksPage } from "./tasks/TasksPages";
import SettingsPage from "./setup/SettingsPage";
import WorkspaceOverview from "./setup/WorkspaceOverview";
import WorkspaceActivity from "./setup/WorkspaceActivity";
import ReviewsPage from "./reviews/ReviewsPage";
import StartDecisionReview from "./reviews/StartDecisionReview";
import DecisionReviewDetail from "./reviews/DecisionReviewDetail";
import FocusedReviewPage from "./reviews/FocusedReviewPage";
import "./App.css";
import "./CreditPurchase.css";
import "./Navigation.css";
import "./RepoAnalyzer.css";
import "./UserDashboard.css";
import "./Auth.css";
import "./FileContent.css";
import "./Modal.css";
import "./ErrorBoundary.css";

const MainContent = () => {
  const { user, loading, logout, activeOrganization } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeRepo, setActiveRepo] = useState(null);
  const [repoMissing, setRepoMissing] = useState(false);
  const [repositories, setRepositories] = useState([]);
  const place = parseProductLocation(location.pathname, location.search);
  const taskLocation = parseTaskLocation(location.pathname, location.search);
  const permissionUser = {
    ...user,
    organizationRole: activeOrganization?.role || user?.organizationRole,
  };

  const organizationId = activeOrganization?.organizationId || "";
  const loadRepositories = useCallback(async () => {
    if (!user) return [];
    try {
      const response = await api.get("/users/profile", {
        headers: organizationId ? { "X-Organization-Id": organizationId } : undefined,
      });
      const rows = response.data?.repos || [];
      setRepositories(rows);
      return rows;
    } catch {
      setRepositories([]);
      return [];
    }
  }, [user, organizationId]);

  useEffect(() => {
    let cancelled = false;
    if (!user) return undefined;
    loadRepositories().catch(() => {
      if (!cancelled) setRepositories([]);
    });
    return () => {
      cancelled = true;
    };
  }, [user, loadRepositories]);

  useEffect(() => {
    if (place.kind !== "repository" || !taskLocation.repositoryId) {
      setRepoMissing(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        if (activeRepo && String(activeRepo._id) === String(taskLocation.repositoryId)) {
          setRepoMissing(false);
          return;
        }
        const repo = await getRepo(taskLocation.repositoryId);
        if (cancelled || !repo?._id) return;
        setActiveRepo(repo);
        setRepoMissing(false);
      } catch {
        if (!cancelled) setRepoMissing(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeRepo is a same-id cache
  }, [place.kind, taskLocation.repositoryId]);

  const experimental = process.env.REACT_APP_EXPERIMENTAL_TASKS === "true";
  const repositoryChoices = uniqueRepositories(repositories.length ? repositories : activeRepo ? [activeRepo] : []);
  const activeRepositoryIds = repositoryIdsFor(repositoryChoices, place.repositoryId);
  const defaultRepositoryId = repositoryChoices[0]?._id ? String(repositoryChoices[0]._id) : "";

  useEffect(() => {
    if (!defaultRepositoryId || place.repositoryId) return;
    const scoped = place.kind === "reviews" || String(place.kind).startsWith("guide") || place.kind === "tasks" || place.kind === "task" || place.kind === "task-new";
    if (!scoped) return;
    if (place.kind === "guides") {
      const params = new URLSearchParams(location.search);
      params.set("repository", defaultRepositoryId);
      navigate(`/brain?${params.toString()}`, { replace: true });
      return;
    }
    if (String(place.kind).startsWith("guide")) {
      navigate(`/brain?repository=${defaultRepositoryId}`, { replace: true });
      return;
    }
    if (place.kind === "tasks" || place.kind === "task" || place.kind === "task-new") {
      navigate(tasksPath({ filter: place.kind === "tasks" ? place.filter : "needs-me", repositoryId: defaultRepositoryId }), { replace: true });
      return;
    }
    const reviewFilter = place.filter === "running" || place.filter === "all" ? place.filter : "current";
    navigate(reviewsPath({ filter: reviewFilter, type: place.type, repositoryId: defaultRepositoryId }), { replace: true });
  }, [defaultRepositoryId, place.repositoryId, place.kind, place.filter, place.type, location.search, navigate]);

  if (location.pathname === "/invite/accept") {
    return <InviteAccept />;
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/register" element={<Auth />} />
        <Route path="/signup" element={<Auth />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invite/accept" element={<InviteAccept />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const handleRepoSubmit = (repo) => {
    setActiveRepo(repo);
    if (repo?._id) navigate(taskPath({ repositoryId: repo._id }));
  };

  const handleOpenNewRepository = (prefill) => {
    if (prefill) {
      try {
        sessionStorage.setItem("renderrepo.newRepoPrefill", prefill);
      } catch {
        // ignore quota
      }
    }
    setActiveRepo(null);
    navigate("/tools/new");
  };

  return (
    <ErrorBoundary
      showDetails={process.env.NODE_ENV === "development"}
      onReset={() => {
        setActiveRepo(null);
        navigate("/");
      }}
    >
      <AppShell
        area={place.kind}
        pathname={location.pathname}
        user={permissionUser}
        workspaceName={activeOrganization?.name || ""}
        repositories={repositoryChoices}
        repositoryId={place.repositoryId}
        allowAllCodebases={["workspace", "activity", "reviews", "settings"].includes(place.kind)}
        onRepository={(repositoryId) => {
          if (place.kind === "workspace") {
            navigate(repositoryId ? `/workspace?repository=${repositoryId}` : "/workspace");
            return;
          }
          if (place.kind === "activity") {
            navigate(repositoryId ? `/activity?repository=${repositoryId}` : "/activity");
            return;
          }
          if (place.kind === "settings") {
            navigate(repositoryId ? `/settings?repository=${repositoryId}${location.hash || ""}` : `/settings${location.hash || ""}`);
            return;
          }
          if (String(place.kind).startsWith("guide")) {
            navigate(repositoryId ? `/brain?repository=${repositoryId}` : "/brain");
            return;
          }
          if (place.kind === "tasks" || place.kind === "task" || place.kind === "task-new") {
            navigate(tasksPath({ filter: place.kind === "tasks" ? place.filter : "needs-me", repositoryId }));
            return;
          }
          const reviewFilter = place.filter === "running" || place.filter === "all" || place.filter === "attention" ? place.filter : "current";
          navigate(reviewsPath({ filter: reviewFilter, type: place.type, repositoryId }));
        }}
        mutateAllowed={canMutateRepos(permissionUser)}
        onLogout={logout}
      >
        <DemoTaskImportPrompt />
        {experimental && place.kind === "tools" ? (
          <header className="rr-page-header">
            <p className="rr-kicker">Experimental</p>
            <h1>Repository tools</h1>
          </header>
        ) : null}
        {location.pathname === "/" || location.pathname === "/dashboard" ? <Navigate to={`/workspace${location.search}`} replace /> : null}
        {!experimental && ["tasks", "task", "task-new", "tools", "new-repo"].includes(place.kind) ? <Navigate to="/workspace" replace /> : null}
        <div className={experimental && place.kind === "tools" ? undefined : "view-panel-hidden"}>
          <UserDashboard onSelectRepo={handleRepoSubmit} />
        </div>

        {["guides", "guide", "guide-suggestions", "guide-suggestion", "guide-conflicts", "guide-conflict", "guide-history"].includes(place.kind) ? (
          <GuidesPage
            repositories={repositoryChoices}
            section={place.kind}
            guideId={place.guideId || ""}
            suggestionId={place.suggestionId || ""}
            conflictId={place.conflictId || ""}
            repositoryId={place.repositoryId}
            filters={{
              category: place.category,
              priority: place.priority,
              status: place.status,
              scope: place.scope,
              q: place.q,
            }}
            mutateAllowed={canMutateRepos(permissionUser)}
            requestedLens={place.lens}
            adminAllowed={["owner", "admin"].includes(permissionUser.organizationRole)}
            prefill={place.guideId === "new" ? {
              title: new URLSearchParams(location.search).get("title") || "",
              body: new URLSearchParams(location.search).get("body") || "",
              category: new URLSearchParams(location.search).get("category") || "product_context",
              rationale: new URLSearchParams(location.search).get("rationale") || "",
              pathScope: new URLSearchParams(location.search).get("scope") || "",
            } : null}
            onOpen={({ guideId, repositoryId, section = "guides", suggestionId, conflictId }) => {
              const repo = repositoryId ? `?repository=${repositoryId}` : "";
              if (section === "suggestions") navigate(suggestionId ? `/brain/suggestions/${suggestionId}${repo}` : `/brain/suggestions${repo}`);
              else if (section === "conflicts") navigate(conflictId ? `/brain/conflicts/${conflictId}${repo}` : `/brain/conflicts${repo}`);
              else if (section === "history" && guideId) navigate(`/brain/${guideId}/history${repo}`);
              else if (!guideId) navigate(repositoryId ? `/brain?repository=${repositoryId}` : "/brain");
              else navigate(`/brain/${guideId}${repo}`);
            }}
            onCreated={(entry) => {
              if (entry?._id) navigate(`/brain/${entry._id}?repository=${entry.repoId || place.repositoryId}`);
            }}
          />
        ) : null}

        {experimental && place.kind === "tasks" ? (
          <TasksPage
            filter={place.filter}
            repositoryId={place.repositoryId}
            repositoryIds={activeRepositoryIds}
            onFilter={(filter) => navigate(tasksPath({ filter, repositoryId: place.repositoryId }))}
            onNewTask={() => navigate(place.repositoryId ? `/tasks/new?repository=${place.repositoryId}` : "/tasks/new")}
          />
        ) : null}

        {experimental && place.kind === "task-new" ? (
          <NewTaskPage
            repositories={repositoryChoices}
            repositoryId={place.repositoryId}
            guideId={place.guideId}
            mutateAllowed={canMutateRepos(permissionUser)}
            onCreated={(task) => {
              if (task?.id) navigate(`/tasks/${task.id}`);
            }}
          />
        ) : null}

        {experimental && place.kind === "task" ? <TaskDetailPage taskId={place.taskId} section={place.section || "overview"} /> : null}

        {place.kind === "workspace" && location.pathname !== "/" && location.pathname !== "/dashboard" ? (
          <WorkspaceOverview
            mutateAllowed={canMutateRepos(permissionUser)}
            canConnect={canManageIntegrations(permissionUser)}
            repositories={repositoryChoices}
            onRepositoryAdded={(repository) => {
              setRepositories((current) => uniqueRepositories([...current, repository]));
              void loadRepositories();
            }}
          />
        ) : null}

        {place.kind === "activity" ? <WorkspaceActivity /> : null}

        {place.kind === "reviews" ? (
          <ReviewsPage
            filter={place.filter}
            type={place.type}
            repositoryId={place.repositoryId}
            repositoryIds={activeRepositoryIds}
            repositoryCount={repositoryChoices.length}
            mutateAllowed={canMutateRepos(permissionUser)}
            onFilter={(filter) => navigate(reviewsPath({ filter, type: place.type, repositoryId: place.repositoryId }))}
            onType={(type) => navigate(reviewsPath({ filter: place.filter, type, repositoryId: place.repositoryId }))}
            onStarted={(review) => {
              if (review?.id) navigate(`/reviews/${review.id}`);
            }}
            onOpenDecision={(item) => navigate(`/reviews/${item.id}`)}
            onOpenAcceptance={(item) => {
              if (item.repoId && item.featurePlanId) {
                navigate(taskPath({ repositoryId: item.repoId, taskId: item.featurePlanId, section: "review" }));
              }
            }}
          />
        ) : null}

        {place.kind === "start" ? (
          <StartDecisionReview
            mutateAllowed={canMutateRepos(permissionUser)}
            onStarted={(review, existing) => {
              if (review?.id) navigate(`/reviews/${review.id}`, { state: { alreadyReviewed: existing } });
            }}
          />
        ) : null}

        {place.kind === "decision" ? (
          <DecisionReviewDetail
            reviewId={place.reviewId}
            mutateAllowed={canMutateRepos(permissionUser)}
            alreadyReviewed={Boolean(location.state?.alreadyReviewed)}
            onTaskCreated={(task) => {
              if (task?.id) navigate(`/tasks/${task.id}`);
            }}
          />
        ) : null}

        {place.kind === "settings" ? (
          <SettingsPage
            canConnect={canManageIntegrations(permissionUser)}
            repositories={repositoryChoices}
            onRepositoryAdded={(repository) => {
              setRepositories((current) => uniqueRepositories([...current, repository]));
              if (repository?._id) navigate(`/settings?repository=${repository._id}#repositories`);
              void loadRepositories();
            }}
          />
        ) : null}

        {place.kind === "repository" && repoMissing ? (
          <div data-testid="repository-not-found">
            <h1>This repository is not available</h1>
            <p>It may belong to another workspace, or the link may be incorrect.</p>
            <button type="button" className="rr-button" onClick={() => navigate("/")}>
              Back to Reviews
            </button>
          </div>
        ) : null}

        {place.kind === "repository" && activeRepo && !repoMissing && taskLocation.section === "review" && taskLocation.taskId ? (
          <FocusedReviewPage
            repo={activeRepo}
            taskId={taskLocation.taskId}
            experimental={experimental}
          />
        ) : null}

        {place.kind === "repository" && activeRepo && !repoMissing && !(taskLocation.section === "review" && taskLocation.taskId) ? (
          <RepoAnalyzer
            initialRepo={activeRepo}
            initialTaskId={taskLocation.taskId}
            initialTaskSection={taskLocation.section}
            initialTaskDiscuss={taskLocation.discuss}
            initialTaskFocus={taskLocation.focus}
            initialTaskItem={taskLocation.item}
            initialTasksFilter={taskLocation.view === "reviews" ? "review" : "all"}
            onRepoSubmitted={handleRepoSubmit}
            onOpenNewRepository={handleOpenNewRepository}
            onTaskRoute={navigate}
          />
        ) : null}

        {experimental && place.kind === "new-repo" ? <RepoAnalyzer onRepoSubmitted={handleRepoSubmit} /> : null}
        {place.kind === "admin" ? <AdminDashboard /> : null}
        {place.kind === "about" ? <About /> : null}
      </AppShell>
    </ErrorBoundary>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <MainContent />
    </AuthProvider>
  );
};

export default App;
